import { useEffect, useState } from "react";
import { FlameMark } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function GoogleSignInButton({ redirectTo = "/account", label = "Continue with Google" }) {
  return (
    <button
      data-testid="google-signin-button"
      type="button"
      onClick={() => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + redirectTo;
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      }}
      className="flex w-full items-center justify-center gap-3 rounded-full border border-starlight/20 bg-white/5 py-3.5 text-sm font-semibold text-starlight transition-colors hover:border-ember/60 hover:bg-white/10"
    >
      <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M23.5 12.3c0-.85-.08-1.7-.24-2.5H12v4.75h6.47c-.28 1.5-1.13 2.77-2.41 3.62v3h3.9c2.29-2.1 3.54-5.2 3.54-8.87z" />
        <path fill="#34A853" d="M12 24c3.24 0 5.95-1.07 7.93-2.91l-3.9-3c-1.08.73-2.46 1.16-4.03 1.16-3.1 0-5.72-2.1-6.66-4.9H1.32v3.09C3.3 21.4 7.31 24 12 24z" />
        <path fill="#FBBC05" d="M5.34 14.35c-.23-.69-.36-1.43-.36-2.2s.13-1.5.36-2.2V6.86H1.32C.48 8.69 0 10.34 0 12.15s.48 3.46 1.32 5.29l4.02-3.09z" />
        <path fill="#EA4335" d="M12 4.8c1.76 0 3.34.61 4.58 1.8l3.44-3.44C17.94 1.13 15.24 0 12 0 7.31 0 3.3 2.6 1.32 6.55l4.02 3.1C6.28 6.9 8.9 4.8 12 4.8z" />
      </svg>
      {label}
    </button>
  );
}

export default function Login({ redirectTo = "/account" }) {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email: email.trim(), password } : { name: name.trim(), email: email.trim(), password };
      const res = await fetch(`${API}${path}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(formatApiErrorDetail(data?.detail));
      window.location.href = redirectTo;
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "mt-1.5 w-full rounded-xl border border-starlight/15 bg-white/5 px-4 py-3 text-sm text-starlight placeholder:text-muted-night/50 outline-none focus:border-ember/70";

  return (
    <div className="min-h-screen bg-obsidian text-starlight flex items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-3xl border border-ember/25 bg-card p-8">
        <div className="flex items-center gap-3 justify-center">
          <FlameMark className="h-10 w-10" />
          <div>
            <p className="font-display font-bold tracking-[0.18em] text-starlight text-lg">UTSAV</p>
            <p className="font-mono text-[10px] tracking-[0.42em] text-ember">YOUR ACCOUNT</p>
          </div>
        </div>
        <p className="mt-6 text-center text-sm text-muted-night">
          {mode === "login" ? "Sign in to track orders and check out faster." : "Create your account to save orders and addresses."}
        </p>

        <div className="mt-6">
          <GoogleSignInButton redirectTo={redirectTo} />
        </div>

        <div className="mt-5 flex items-center gap-3">
          <span className="h-px flex-1 bg-starlight/15" />
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-night">or</span>
          <span className="h-px flex-1 bg-starlight/15" />
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4" data-testid="customer-auth-form">
          {mode === "register" && (
            <div>
              <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-muted-night">Name</label>
              <input data-testid="customer-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={inputCls} />
            </div>
          )}
          <div>
            <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-muted-night">Email</label>
            <input data-testid="customer-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={inputCls} />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-muted-night">Password</label>
            <input data-testid="customer-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === "register" ? "6+ characters" : "••••••••"} className={inputCls} />
          </div>

          {error && (
            <p className="rounded-xl border border-crimson/40 bg-crimson/10 px-4 py-3 text-xs text-starlight" data-testid="customer-auth-error">
              {error}
            </p>
          )}

          <button
            data-testid="customer-auth-submit"
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-ember py-3.5 font-mono text-xs font-bold tracking-[0.22em] uppercase text-obsidian hover:bg-marigold transition-colors disabled:opacity-50"
          >
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <button
          data-testid="customer-auth-toggle"
          onClick={() => setMode((m) => (m === "login" ? "register" : "login"))}
          className="mt-5 block w-full text-center font-mono text-[11px] tracking-[0.2em] uppercase text-muted-night hover:text-ember transition-colors"
        >
          {mode === "login" ? "New here? Create an account" : "Have an account? Sign in"}
        </button>
        <a href="/" className="mt-3 block text-center font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night/70 hover:text-ember transition-colors">
          ← Back to store
        </a>
      </div>
    </div>
  );
}
